import { Select } from '@backstage/core-components';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { notificationsTranslationRef } from '@backstage/plugin-notifications';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ComponentProps } from 'react';
import { NotificationsFiltersState } from './useNotificationsPage';

const useStyles = makeStyles(theme => ({
  picker: {
    // Use Catalog's actual Select and its portalled MUI menu, with the
    // borderless field requested for this page. No popup overrides are needed.
    '& [class*="BackstageSelectInputBase-input"]': {
      border: 0,
    },
    '& [role="button"]:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: theme.spacing(0.5),
    },
  },
}));

const ALL_TOPICS = '___all___';

export const CustomNotificationsFilters = (
  props: NotificationsFiltersState,
) => {
  const classes = useStyles();
  const { t } = useTranslationRef(notificationsTranslationRef);
  const picker = (selectProps: ComponentProps<typeof Select>) => (
    <Box
      key={selectProps.label}
      pt={1}
      pb={1}
      className={classes.picker}
      role="group"
      aria-label={selectProps.label}
    >
      <Select {...selectProps} />
    </Box>
  );

  return (
    <>
      {picker({
        label: t('filters.view.label'),
        selected: props.view,
        onChange: value => props.setView(String(value) as typeof props.view),
        items: [
          { value: 'unread', label: t('filters.view.unread') },
          { value: 'read', label: t('filters.view.read') },
          { value: 'saved', label: t('filters.view.saved') },
          { value: 'all', label: t('filters.view.all') },
        ],
      })}
      {picker({
        label: t('filters.createdAfter.label'),
        selected: props.createdAfter,
        onChange: value =>
          props.setCreatedAfter(String(value) as typeof props.createdAfter),
        items: [
          { value: 'last24h', label: t('filters.createdAfter.last24h') },
          { value: 'lastWeek', label: t('filters.createdAfter.lastWeek') },
          { value: 'all', label: t('filters.createdAfter.anyTime') },
        ],
      })}
      {picker({
        label: t('filters.sortBy.label'),
        selected: props.sortBy,
        onChange: value =>
          props.setSortBy(String(value) as typeof props.sortBy),
        items: [
          { value: 'newest', label: t('filters.sortBy.newest') },
          { value: 'oldest', label: t('filters.sortBy.oldest') },
          { value: 'topic', label: t('filters.sortBy.topic') },
          { value: 'origin', label: t('filters.sortBy.origin') },
        ],
      })}
      {picker({
        label: t('filters.severity.label'),
        selected: props.severity,
        onChange: value =>
          props.setSeverity(String(value) as typeof props.severity),
        items: (['critical', 'high', 'normal', 'low'] as const).map(value => ({
          value,
          label: t(`filters.severity.${value}`),
        })),
      })}
      {picker({
        label: t('filters.topic.label'),
        selected: props.topic ?? ALL_TOPICS,
        onChange: value =>
          props.setTopic(value === ALL_TOPICS ? undefined : String(value)),
        items: [
          { value: ALL_TOPICS, label: t('filters.topic.anyTopic') },
          ...[...(props.allTopics ?? [])]
            .sort((a, b) => a.localeCompare(b))
            .map(value => ({ value, label: value })),
        ],
      })}
    </>
  );
};
